import React, { useState, useRef } from 'react';
import { uploadApi } from '../api/upload.api';
import { RecommendedImageSize } from '../api/section.api';
import ImageCropModal from './ImageCropModal';
import PhotoSourceChooser, { isCameraCaptureSupported } from './PhotoSourceChooser';
import { useNotify } from '../context/NotifyContext';

/** Mirrors the server's upload allowlist (see upload.router.ts). */
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPTED_IMAGE_MIMETYPES = ACCEPTED_IMAGE_TYPES.split(',');

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  folder?: string;
  /** Ideal upload dimensions for this specific field, shown as a helper hint (e.g. "Recommended: 800 × 600px"). */
  recommendedSize?: RecommendedImageSize;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label, folder = 'images', recommendedSize }) => {
  const { notify } = useNotify();
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || '');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Separate input purely so it can carry `capture`, which is what asks the OS
  // for the camera rather than the photo library.
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Pending pick, waiting to be cropped. Nothing is uploaded until the user confirms the
  // crop — there's no "skip crop" path, so every uploaded image respects the field's
  // recommended aspect ratio.
  const [pendingCrop, setPendingCrop] = useState<{ src: string; fileName: string; mimeType: string } | null>(null);
  const [sourceChooserOpen, setSourceChooserOpen] = useState(false);

  const aspect = recommendedSize ? recommendedSize.width / recommendedSize.height : undefined;
  const recommendedHint = recommendedSize
    ? `Recommended: ${recommendedSize.width} × ${recommendedSize.height}px${recommendedSize.note ? ` (${recommendedSize.note})` : ''}`
    : undefined;

  /**
   * Validates an image and hands it to the crop step, whether it came from the
   * device or straight off the camera. Camera shots need the crop just as much
   * as picked files do — a raw frame is 4:3 or 16:9, which is nothing like the
   * ratio a logo or a card image expects.
   */
  const acceptFile = (file: File) => {
    // Validate file type against the server's upload allowlist (see
    // upload.router.ts). Narrower than `image/*`: HEIC/SVG/TIFF can't be read
    // by the vision models or rendered by every browser, and HEIC in
    // particular is what an iPhone stores natively.
    if (!ACCEPTED_IMAGE_MIMETYPES.includes(file.type.toLowerCase())) {
      notify.error('Please use a JPG, PNG, WebP or GIF image. On iPhone, pick the photo from your Photos library rather than the Files app.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notify.error('File size must be less than 10MB');
      return;
    }

    const src = URL.createObjectURL(file);
    setPendingCrop({ src, fileName: file.name, mimeType: file.type });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    e.target.value = '';
    if (!file) return;
    acceptFile(file);
  };

  /**
   * One "Add photo" button that asks for the source, rather than one button per
   * source. Where there is no camera to offer there is only one real option, so
   * the chooser is skipped and the file picker opens directly.
   */
  const handleAddPhotoClick = () => {
    if (isCameraCaptureSupported) {
      setSourceChooserOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadApi.uploadFile(file, folder);
      onChange(url);
      setUrlInput(url);
      setShowUrlInput(false);
    } catch (error: any) {
      notify.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    if (pendingCrop) URL.revokeObjectURL(pendingCrop.src);
    setPendingCrop(null);
  };

  const handleCropConfirm = (cropped: File) => {
    if (pendingCrop) URL.revokeObjectURL(pendingCrop.src);
    setPendingCrop(null);
    uploadFile(cropped);
  };

  const handleUrlSubmit = () => {
    onChange(urlInput);
    setShowUrlInput(false);
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-nova-text2">
          {label}
        </label>
      )}
      {recommendedSize && (
        <p className="text-xs text-nova-text3 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Recommended size: {recommendedSize.width} &times; {recommendedSize.height}px
          {recommendedSize.note ? ` (${recommendedSize.note})` : ''}
        </p>
      )}

      {/* Image Preview */}
      {value && (
        <div className="relative w-full max-w-xs">
          <img
            src={value}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-nova-border"
            onError={(e) => {
              e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Invalid+Image';
            }}
          />
          <button
            type="button"
            onClick={() => {
              onChange('');
              setUrlInput('');
            }}
            className="absolute top-2 right-2 p-2.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Options */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleAddPhotoClick}
          disabled={uploading}
          className="flex-1 sm:flex-none min-w-[140px] px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center"
        >
          {uploading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Add photo
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="flex-1 sm:flex-none min-w-[120px] px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors justify-center"
        >
          {showUrlInput ? 'Cancel' : 'Use URL'}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          onChange={handleFileSelect}
          className="hidden"
        />
        {/* `capture` hands off to the phone's own camera app, which avoids
            building a viewfinder — and the shutter, the preview and the
            orientation handling that come with it. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* URL Input */}
      {showUrlInput && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Enter image URL"
            className="flex-1 px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky min-w-0"
          />
          <button
            type="button"
            onClick={handleUrlSubmit}
            className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors w-full sm:w-auto justify-center"
          >
            Set URL
          </button>
        </div>
      )}

      {sourceChooserOpen && (
        <PhotoSourceChooser
          onPickCamera={() => {
            setSourceChooserOpen(false);
            cameraInputRef.current?.click();
          }}
          onPickGallery={() => {
            setSourceChooserOpen(false);
            fileInputRef.current?.click();
          }}
          onCancel={() => setSourceChooserOpen(false)}
        />
      )}

      {pendingCrop && (
        <ImageCropModal
          imageSrc={pendingCrop.src}
          fileName={pendingCrop.fileName}
          mimeType={pendingCrop.mimeType}
          aspect={aspect}
          recommendedHint={recommendedHint}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
};

export default ImageUpload;
